import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Clock, Truck, Utensils,
  Loader2, AlertCircle,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

import { usePublicRestaurant } from '@/features/storefront/hooks/usePublicRestaurant';
import { usePublicRestaurantLanguage, toI18nLanguage } from '@/features/storefront/hooks/usePublicRestaurantLanguage';
import { getEstimatedTimeForNow, formatEstimatedTimeRange } from '@/features/storefront/utils/estimatedTime';
import PublicFooter from '@/features/storefront/components/PublicFooter';
import PublicNavbar from '@/features/storefront/components/PublicNavbar';
import { getStoreStatusForNavbar, getTodayHours } from '@/features/storefront/utils/storeStatus';
import { useCartCountFromStorage } from '@/features/storefront/store';
import { usePublicDiscounts } from '@/features/storefront/hooks/usePublicDiscounts';
import type { PromoItem } from '@/features/storefront/components/PromotionalCarousel';
import { useIsMobile } from '@/shared/hooks/use-mobile';
import CookieBanner from '@/features/storefront/components/CookieBanner';
import PromoBanner from '@/features/storefront/components/PromoBanner';
import { WhatsAppFab } from '@/features/storefront/components/WhatsAppFab';
import { LandingHeroSection } from '@/features/storefront/components/LandingHeroSection';
import { LandingContactSection } from '@/features/storefront/components/LandingContactSection';
import { RestaurantSeoHead } from '@/shared/components/RestaurantSeoHead';

// Lazy load heavy components for better performance
const AboutSection = lazy(() => import('@/features/storefront/components/AboutSection'));
const GallerySection = lazy(() => import('@/features/storefront/components/GallerySection'));
const LandingDeliveryCheck = lazy(() => import('@/features/storefront/components/LandingDeliveryCheck'));
const PromotionalCarousel = lazy(() => import('@/features/storefront/components/PromotionalCarousel'));


const PublicLanding = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { restaurantId } = useParams();
  const { restaurant, isLoading, isError, error } = usePublicRestaurant(restaurantId);
  const cartCount = useCartCountFromStorage(restaurantId);
  usePublicRestaurantLanguage(restaurant);
  const { data: discounts = [] } = usePublicDiscounts(restaurantId);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const [deliveryCheckOpen, setDeliveryCheckOpen] = useState(false);



  // Prepare gallery items from restaurant data
  const carouselItems = useMemo(() => {
    if (!restaurant) return [];

    // Use galleryPhotos if available
    if (restaurant.galleryPhotos && restaurant.galleryPhotos.length > 0) {
      return restaurant.galleryPhotos.slice(0, 10).map((url, index) => ({
        id: `gallery-${index}`,
        title: `${t('landing.galleryPhoto')} ${index + 1}`,
        subtitle: restaurant.name || '',
        url: url
      }));
    }

    // Fallback to cover image
    if (restaurant.coverImageUrl) {
      return [{
        id: 'cover',
        title: restaurant.name || t('landing.restaurantDefault'),
        subtitle: restaurant.subHeading || restaurant.description || '',
        url: restaurant.coverImageUrl
      }];
    }

    return [];
  }, [restaurant, t]);

  const promoItems = useMemo<PromoItem[]>(() => {
    const currency = restaurant?.currency || 'EUR';
    const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
    return discounts
      .filter((d) => d.isActive)
      .map((d) => ({
        id: d.discountId,
        title: d.name,
        description: d.description,
        imageUrl: d.imageUrl ?? undefined,
        discount:
          d.discountType === 'PERCENTAGE' ? `${d.discountValue}% OFF`
          : d.discountType === 'FIXED_AMOUNT' ? `${symbol}${d.discountValue} OFF`
          : d.discountType === 'FLAT_RATE' ? `${symbol}${d.discountValue} flat`
          : d.discountType === 'COMBO' ? `${symbol}${d.comboPrice}`
          : d.discountType === 'BUY_X_GET_Y' && d.buyXGetY
            ? `Buy ${d.buyXGetY.buyQuantity} Get ${d.buyXGetY.getQuantity}`
          : undefined,
        discountedPrice: d.discountType === 'COMBO' ? d.comboPrice : undefined,
        includedItems: d.discountType === 'COMBO' && d.comboItems
          ? d.comboItems.map((ci) => ({ name: ci.menuItemName ?? ci.categoryName ?? '', quantity: ci.quantity }))
          : undefined,
        daysOfWeek: d.daysOfWeek,
        startTime: d.startTime,
        endTime: d.endTime,
        startDate: d.startDate,
        endDate: d.endDate,
        allowedOrderTypes: d.allowedOrderTypes,
        isActive: d.isActive,
      }));
  }, [discounts, restaurant?.currency]);

  // Auto-advance carousel
  useEffect(() => {
    if (carouselItems.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselItems.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);

  // Build Quick Info cards from restaurant data
  type InfoCard = { label: string; value: string; icon: React.ComponentType<{ className?: string }>; accentClass: string; onClick?: () => void };
  const infoCards = useMemo<InfoCard[]>(() => {
    if (!restaurant) return [];
    const formatCurrencyInner = (amount: number | undefined): string => {
      if (amount === undefined || amount === null) return 'N/A';
      const currency = restaurant.currency || 'EUR';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
    };
    return ([
      restaurant.hasDelivery && restaurant.isDeliveryOpen && restaurant.deliveryZones?.length && {
        label: t('landing.deliveryRadius'),
        value: `${t('landing.upTo')} ${Math.max(...restaurant.deliveryZones.map(z => z.maxDistanceKm))} km`,
        icon: Truck, accentClass: 'bg-blue-50 text-blue-500',
        onClick: () => setDeliveryCheckOpen(true),
      },
      restaurant.hasDelivery && restaurant.isDeliveryOpen && restaurant.deliveryMinOrderAmount && {
        label: t('landing.minOrder'),
        value: formatCurrencyInner(restaurant.deliveryMinOrderAmount),
        icon: ShoppingCart, accentClass: 'bg-violet-50 text-violet-500',
        onClick: () => setDeliveryCheckOpen(true),
      },
      restaurant.hasDelivery && restaurant.isDeliveryOpen && (restaurant.deliveryZones?.length ? true : restaurant.deliveryFee !== undefined) && {
        label: t('landing.deliveryFeeLabel'),
        value: restaurant.deliveryZones?.length
          ? (restaurant.deliveryZones.every(z => z.deliveryFee === 0)
            ? t('landing.free')
            : `${t('landing.from')} ${formatCurrencyInner(Math.min(...restaurant.deliveryZones.map(z => z.deliveryFee)))}`)
          : (restaurant.deliveryFee === 0 ? t('landing.free') : formatCurrencyInner(restaurant.deliveryFee!)),
        icon: Truck, accentClass: 'bg-emerald-50 text-emerald-500',
        onClick: () => setDeliveryCheckOpen(true),
      },
      restaurant.hasDelivery && restaurant.isDeliveryOpen && (() => {
        const est = getEstimatedTimeForNow(restaurant.deliveryTimeRanges);
        return est ? { label: t('landing.deliveryTimeEstimate', 'Delivery time'), value: formatEstimatedTimeRange(est.minMinutes, est.maxMinutes), icon: Truck, accentClass: 'bg-cyan-50 text-cyan-500', onClick: () => setDeliveryCheckOpen(true) } : null;
      })(),
      restaurant.hasPickup && (() => {
        const est = getEstimatedTimeForNow(restaurant.pickupTimeRanges);
        return est ? { label: t('landing.pickupTimeEstimate', 'Pickup ready'), value: formatEstimatedTimeRange(est.minMinutes, est.maxMinutes), icon: Utensils, accentClass: 'bg-orange-50 text-orange-500', onClick: () => navigate(`/${restaurantId}/menu`) } : null;
      })(),
      restaurant.openingHours && {
        label: t('landing.hoursToday'),
        value: getTodayHours(restaurant.openingHours) ?? t('landing.closedToday'),
        icon: Clock, accentClass: 'bg-amber-50 text-amber-500',
      },
    ] as (InfoCard | false | null | undefined)[]).filter(Boolean) as InfoCard[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant, t, restaurantId]);

  const infoTrackRef = useRef<HTMLDivElement>(null);
  const infoDrag = useRef<{ startX: number; startOffset: number } | null>(null);
  const INFO_DURATION = 120;

  const infoStartDrag = (clientX: number) => {
    const el = infoTrackRef.current;
    if (!el) return;
    const currentX = new DOMMatrix(window.getComputedStyle(el).transform).m41;
    el.style.animationPlayState = 'paused';
    el.style.transform = `translateX(${currentX}px)`;
    infoDrag.current = { startX: clientX, startOffset: currentX };
  };

  const infoMoveDrag = (clientX: number) => {
    const el = infoTrackRef.current;
    if (!el || !infoDrag.current) return;
    el.style.transform = `translateX(${infoDrag.current.startOffset + clientX - infoDrag.current.startX}px)`;
  };

  const infoEndDrag = () => {
    const el = infoTrackRef.current;
    if (!el || !infoDrag.current) return;
    const currentX = new DOMMatrix(el.style.transform).m41;
    const halfTrack = el.offsetWidth / 2;
    let nx = currentX % -halfTrack;
    if (nx > 0) nx -= halfTrack;
    el.style.transform = '';
    el.style.animationDelay = `${(nx / halfTrack) * INFO_DURATION}s`;
    el.style.animationPlayState = 'running';
    infoDrag.current = null;
  };


  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-foreground" />
          <p className="text-muted-foreground">{t('landing.loadingRestaurant')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message || t('landing.failedToLoadRestaurant')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0 safe-area-bottom relative overflow-x-hidden w-full max-w-[100vw]">
      <RestaurantSeoHead restaurant={restaurant} />
      {/* Crystal background — fine neutral dot grid only */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(0 0 0 / 0.18) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      {/* ── Offers Banner ────────────────────────────────────────────── */}
      <PromoBanner
        restaurantId={restaurantId!}
        currency={restaurant.currency || 'EUR'}
      />

      {/* Navbar */}
      <PublicNavbar
        restaurantId={restaurantId!}
        restaurantName={restaurant.name || t('landing.restaurantDefault')}
        logoUrl={restaurant.logoUrl}
        contactPhone={restaurant.contactPhone}
        variant="landing"
        cartCount={cartCount}
        storeStatus={getStoreStatusForNavbar(restaurant, t, { compact: isMobile }) ?? undefined}
      />

      <LandingHeroSection
        restaurant={restaurant}
        restaurantId={restaurantId!}
        carouselItems={carouselItems}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        prevSlide={prevSlide}
        nextSlide={nextSlide}
        onDeliveryCheckOpen={() => setDeliveryCheckOpen(true)}
      />

      {/* Discount / Promo Cards — before About so they capture attention early */}
      {promoItems.length > 0 && (
        <Suspense fallback={null}>
          <PromotionalCarousel items={promoItems} restaurantId={restaurantId} />
        </Suspense>
      )}

      {/* About Section */}
      {restaurant.description && (
        <section id="about" className="scroll-mt-24">
          <Suspense fallback={<div className="py-16 bg-muted/30"><div className="max-w-6xl mx-auto px-4 text-center">{t('landing.loading')}</div></div>}>
            <AboutSection
              title={t('landing.aboutUs')}
              description={restaurant.description}
              specialties={[]}
            />
          </Suspense>
        </section>
      )}

      {/* Quick Info — CSS-animated marquee, draggable */}
      {infoCards.length > 0 && (
        <section id="info" className="py-6 sm:py-8 md:py-10 scroll-mt-24">
          <div className="max-w-6xl mx-auto px-3 sm:px-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground text-center mb-4 sm:mb-5">
              {t('landing.quickInfo', 'Quick Info')}
            </p>
            <div
              className="relative overflow-hidden cursor-grab active:cursor-grabbing select-none"
              onMouseDown={(e) => infoStartDrag(e.clientX)}
              onMouseMove={(e) => infoMoveDrag(e.clientX)}
              onMouseUp={infoEndDrag}
              onMouseLeave={infoEndDrag}
              onTouchStart={(e) => infoStartDrag(e.touches[0].clientX)}
              onTouchMove={(e) => { e.preventDefault(); infoMoveDrag(e.touches[0].clientX); }}
              onTouchEnd={infoEndDrag}
            >
              <div className="absolute left-0 inset-y-0 w-8 sm:w-12 md:w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 inset-y-0 w-8 sm:w-12 md:w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

              <div ref={infoTrackRef} className="flex w-max animate-info-marquee py-1">
                {Array.from({ length: 8 }, () => infoCards).flat().map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={item.onClick}
                      className="flex items-center gap-2 sm:gap-3 mx-1.5 sm:mx-2.5 shrink-0 p-2.5 sm:p-3.5 rounded-2xl bg-white border border-zinc-100 hover:border-zinc-200 hover:shadow-md transition-all text-left w-36 sm:w-44 md:w-48 group"
                    >
                      <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center shrink-0 ${item.accentClass}`}>
                        <Icon className="w-3.5 h-3.5 sm:w-[17px] sm:h-[17px]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[9px] sm:text-[10px] font-semibold text-muted-foreground uppercase tracking-wide truncate">{item.label}</p>
                        <p className="font-bold text-xs sm:text-sm text-foreground truncate">{item.value}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <style>{`
            @keyframes info-marquee {
              from { transform: translateX(0); }
              to   { transform: translateX(-50%); }
            }
            .animate-info-marquee {
              animation: info-marquee 120s linear infinite;
            }
          `}</style>
        </section>
      )}

      {(restaurant.contactPhone || restaurant.contactEmail || restaurant.street || restaurant.city) && (
        <LandingContactSection restaurant={restaurant} />
      )}

      {restaurant.galleryPhotos && restaurant.galleryPhotos.length > 0 && (
        <section id="gallery" className="scroll-mt-24">
          <Suspense fallback={<div className="py-16"><div className="max-w-6xl mx-auto px-4 text-center">{t('landing.loadingGallery')}</div></div>}>
            <GallerySection images={restaurant.galleryPhotos.map((url, index) => ({
              id: `gallery-${index}`,
              url,
              title: `${t('landing.galleryPhoto')} ${index + 1}`,
              category: restaurant.name || t('landing.aboutUs')
            }))} />
          </Suspense>
        </section>
      )}

      <PublicFooter
        restaurantName={restaurant.name || t('landing.restaurantDefault')}
        contactPhone={restaurant.contactPhone}
        contactEmail={restaurant.contactEmail}
        socialLinks={restaurant.socialLinks}
        openingHours={restaurant.openingHours}
        address={{
          street: restaurant.street,
          houseNumber: restaurant.houseNumber,
          postalCode: restaurant.postalCode,
          city: restaurant.city,
          country: restaurant.country,
        }}
        latitude={restaurant.latitude}
        longitude={restaurant.longitude}
        legalPages={restaurant.legalPages}
        impressum={restaurant.impressum}
        datenschutz={restaurant.datenschutz}
        agb={restaurant.agb}
        widerruf={restaurant.widerruf}
        cookie={restaurant.cookie}
        odr={restaurant.odr}
        alkohol={restaurant.alkohol}
      />

      {/* Cookie Banner */}
      <CookieBanner
        content={restaurant.cookie ?? restaurant.legalPages?.cookieBanner}
        lang={toI18nLanguage(restaurant.defaultLanguage)}
      />

      <WhatsAppFab restaurantId={restaurantId!} />


      {/* Delivery check dialog — triggered from the hero CTA row. */}
      {restaurant.hasDelivery && restaurantId && (
        <Dialog open={deliveryCheckOpen} onOpenChange={setDeliveryCheckOpen}>
          <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
            <DialogHeader className="sr-only">
              <DialogTitle>{t('landing.checkDelivery', 'Can we deliver to you?')}</DialogTitle>
              <DialogDescription>
                {t(
                  'landing.checkDeliveryDesc',
                  'Enter your address or use your location to check delivery availability.',
                )}
              </DialogDescription>
            </DialogHeader>
            <Suspense
              fallback={
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {t('landing.loading')}
                </div>
              }
            >
              <LandingDeliveryCheck
                restaurantId={restaurantId}
                restaurantCountry={restaurant.country}
                currency={restaurant.currency}
                isDeliveryOpen={restaurant.isDeliveryOpen}
                onNavigate={() => setDeliveryCheckOpen(false)}
              />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
};

export default PublicLanding;
